import type { ContractServiceAssignmentView } from "@closer/shared";
import { CONTRACT_SERVICE_ASSIGNMENT_STATUS_NAMES } from "@closer/shared";
import { Badge, Button, Card, FormField, Input, Select, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

type AdayYuku = {
  /** İşlemi yapanın KENDİ taraf kaydı — güvenli projeksiyon kimlik taşımıyor. */
  servedPartyId: string | null;
  relationships: { id: string; providerName: string | null; status: string; assignable: boolean }[];
};

/** İlişki durumlarının kullanıcı dili — ekran metin uydurmuyor. */
const DURUM_ADLARI: Record<string, string> = {
  pending: "Hizmet ilişkisi daveti bekleniyor",
  active: "Aktif",
  suspended: "Askıda",
};

/**
 * HUKUKİ HİZMET SAĞLAYICI (CLOS-1185 · rapor §10).
 *
 * ### ⚠ SAĞLAYICI SÖZLEŞMENİN TARAFI DEĞİLDİR
 *
 * Bu kart taraf listesinin YANINDA ayrı duruyor ve dili de ayrı: "bu sözleşmede benim adıma
 * kim çalışıyor?" Sağlayıcıyı taraf kartına koysaydık ekran, sözleşmenin hukuki
 * topolojisine avukatları da eklemiş olurdu.
 *
 * ### ⚠ KARARI HİZMET ALAN TARAF VERİYOR
 *
 * Kart yalnız kendi tarafı için görünür ve yalnız kendi ilişkilerini listeler. Sözleşmenin
 * sahibi bile karşı tarafın vekilini seçemez; karşı tarafın kiminle çalıştığı da bu ekrandan
 * geçmiyor.
 *
 * ### ⚠ ASKIDAKİ İLİŞKİ GİZLENMİYOR, SEBEBİYLE GÖSTERİLİYOR
 *
 * Seçilemeyen bir ilişkiyi listeden düşürseydik kullanıcı "vekilim neden yok?" sorusuyla
 * baş başa kalırdı. Durum yazılıyor, düğme kapalı.
 */
export function SharedServiceProviderSection({ contractId }: { contractId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [secim, setSecim] = useState("");
  const [eposta, setEposta] = useState("");
  const [davetAcik, setDavetAcik] = useState(false);
  /** ⚠ Sonlandırılacak atama + gerekçe: tek tık YOK, hukuki bir görevlendirme sessizce bitmez. */
  const [sonlandirilan, setSonlandirilan] = useState<string | null>(null);
  const [gerekce, setGerekce] = useState("");

  const anahtar = tenantKey(tenantId, "paylasilan-sozlesme", contractId, "saglayici");
  const atamalar = useQuery({
    queryKey: anahtar,
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (
        await api<ContractServiceAssignmentView[]>(
          `/api/shared-contracts/${contractId}/service-assignments`,
          { tenantId },
        )
      ).data,
  });

  const adaylar = useQuery({
    queryKey: tenantKey(tenantId, "paylasilan-sozlesme", contractId, "saglayici-adaylar"),
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (
        await api<AdayYuku>(`/api/shared-contracts/${contractId}/service-assignments/candidates`, {
          tenantId,
        })
      ).data,
  });

  const tazele = async () => {
    await queryClient.invalidateQueries({ queryKey: anahtar });
    await queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "paylasilan-sozlesme", contractId, "saglayici-adaylar"),
    });
  };

  const ata = useMutation({
    mutationFn: (serviceRelationshipId: string) =>
      api(`/api/shared-contracts/${contractId}/service-assignments`, {
        method: "POST",
        tenantId,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          servedPartyId,
          serviceRelationshipId,
          /**
           * ⚠ VARSAYILAN GÜVENLİ KAPSAM. İndirme, onay, mutabakat, imza ve paylaşım
           * yönetimi BİLEREK yok: atama var diye kritik yetki doğmuyor. Kapsam yine de
           * tavan değil, kesişimin bir çarpanı.
           */
          scope: ["contract.view", "contract.revision"],
        }),
      }),
    onSuccess: async () => {
      setSecim("");
      await tazele();
      toast.success("Hizmet sağlayıcı bu sözleşmeye atandı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Atama yapılamadı"),
  });

  const davet = useMutation({
    mutationFn: (email: string) =>
      api(`/api/shared-contracts/${contractId}/service-assignments/invite`, {
        method: "POST",
        tenantId,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          servedPartyId,
          email,
          scope: ["contract.view", "contract.revision"],
        }),
      }),
    onSuccess: async () => {
      setEposta("");
      setDavetAcik(false);
      await tazele();
      toast.success("Hizmet ilişkisi daveti gönderildi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Davet gönderilemedi"),
  });

  /**
   * GÖREVLENDİRMEYİ SONLANDIR (rapor §9.2).
   *
   * ⚠ Bu komut ürüne canlı UAT'ta EKLENDİ: servis yazılıydı, rotası ve düğmesi yoktu.
   * Yani "vekilimi görevden alabilirim" cümlesinin karşılığı üründe yoktu ve yaşam
   * döngüsü testleri atamayı doğrudan veritabanında bitirdiği için boşluk görünmedi.
   */
  const sonlandir = useMutation({
    mutationFn: (girdi: { assignmentId: string; reason: string }) =>
      api(`/api/shared-contracts/${contractId}/service-assignments/${girdi.assignmentId}/end`, {
        method: "POST",
        tenantId,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ reason: girdi.reason }),
      }),
    onSuccess: async () => {
      setSonlandirilan(null);
      setGerekce("");
      await tazele();
      toast.success("Görevlendirme sonlandırıldı");
    },
    onError: (hata: unknown) =>
      toast.error(
        hata instanceof ApiRequestError ? hata.message : "Görevlendirme sonlandırılamadı",
      ),
  });

  /**
   * ⚠ KART, TARAF BAĞI OLMADAN HİÇ ÇİZİLMİYOR. Atama kanonik bir taraf kaydına bağlanmak
   * zorunda; taraf yokken düğme göstermek kullanıcıyı sunucunun reddedeceği bir işe sokardı.
   */
  const servedPartyId = adaylar.data?.servedPartyId ?? null;
  if (!can("contract.view")) return null;
  if (adaylar.isPending) return null;
  if (!servedPartyId) return null;

  const yonetebilir = can("contract.revision");
  const aktifAtamalar = (atamalar.data ?? []).filter((a) => a.status === "active");

  return (
    <Card title="Hukuki Hizmet Sağlayıcı">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted">
          Bu sözleşmede sizin adınıza çalışan hukuki hizmet sağlayıcısını buradan
          görevlendirebilirsiniz. Sağlayıcı sözleşmenin tarafı olmaz.
        </p>

        {atamalar.isPending ? (
          <Skeleton lines={2} />
        ) : atamalar.isError ? (
          <StateView
            state={resolveErrorState(atamalar.error, { idempotent: true })}
            onRetry={() => void atamalar.refetch()}
          />
        ) : aktifAtamalar.length === 0 ? (
          <p className="text-sm text-fg-muted" data-testid="saglayici-yok">
            Bu sözleşme için henüz bir hizmet sağlayıcı görevlendirilmedi.
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="saglayici-atamalari">
            {aktifAtamalar.map((a) => (
              <li key={a.id} className="flex flex-col gap-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-fg">{a.providerName ?? "Sağlayıcı"}</span>
                  <Badge tone="success">{CONTRACT_SERVICE_ASSIGNMENT_STATUS_NAMES[a.status]}</Badge>
                  <span className="text-fg-muted">
                    Bu sözleşmede {a.servedPartyName} adına çalışıyor.
                  </span>
                  {yonetebilir && sonlandirilan !== a.id ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid="saglayici-sonlandir-ac"
                      onClick={() => {
                        setSonlandirilan(a.id);
                        setGerekce("");
                      }}
                    >
                      Görevlendirmeyi sonlandır
                    </Button>
                  ) : null}
                </div>
                {sonlandirilan === a.id ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <FormField label="Sonlandırma gerekçesi">
                      <Input
                        value={gerekce}
                        data-testid="saglayici-sonlandir-gerekce"
                        onChange={(o) => setGerekce(o.target.value)}
                      />
                    </FormField>
                    <Button
                      type="button"
                      size="sm"
                      variant="danger"
                      data-testid="saglayici-sonlandir"
                      loading={sonlandir.isPending}
                      disabled={gerekce.trim().length < 3}
                      onClick={() =>
                        sonlandir.mutate({ assignmentId: a.id, reason: gerekce.trim() })
                      }
                    >
                      Sonlandır
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setSonlandirilan(null)}
                    >
                      Vazgeç
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {yonetebilir ? (
          <>
            {(adaylar.data?.relationships ?? []).length > 0 ? (
              <div className="flex flex-wrap items-end gap-2">
                <FormField label="Mevcut hizmet ilişkileriniz">
                  <Select
                    value={secim}
                    data-testid="saglayici-secim"
                    onChange={(o) => setSecim(o.target.value)}
                  >
                    <option value="">Seçiniz…</option>
                    {(adaylar.data?.relationships ?? []).map((aday) => (
                      <option key={aday.id} value={aday.id} disabled={!aday.assignable}>
                        {aday.providerName ?? "Sağlayıcı"}
                        {aday.status === "active"
                          ? ""
                          : ` — ${DURUM_ADLARI[aday.status] ?? aday.status}`}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <Button
                  type="button"
                  size="sm"
                  data-testid="saglayici-ata"
                  loading={ata.isPending}
                  disabled={secim.length === 0}
                  onClick={() => ata.mutate(secim)}
                >
                  Bu sözleşmeye ata
                </Button>
              </div>
            ) : (
              <p className="text-xs text-fg-soft" data-testid="saglayici-aday-yok">
                Henüz bir hizmet ilişkiniz yok. Aşağıdan yeni bir sağlayıcı davet edebilirsiniz.
              </p>
            )}

            {davetAcik ? (
              <div className="flex flex-wrap items-end gap-2">
                <FormField label="Sağlayıcının e-posta adresi">
                  <Input
                    type="email"
                    value={eposta}
                    data-testid="saglayici-eposta"
                    onChange={(o) => setEposta(o.target.value)}
                  />
                </FormField>
                <Button
                  type="button"
                  size="sm"
                  data-testid="saglayici-davet-gonder"
                  loading={davet.isPending}
                  disabled={eposta.trim().length === 0}
                  onClick={() => davet.mutate(eposta.trim())}
                >
                  Hizmet ilişkisi daveti gönder
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setDavetAcik(false)}>
                  Vazgeç
                </Button>
              </div>
            ) : (
              <div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  data-testid="saglayici-davet-ac"
                  onClick={() => setDavetAcik(true)}
                >
                  Yeni hukuki hizmet sağlayıcı davet et
                </Button>
              </div>
            )}
          </>
        ) : null}
      </div>
    </Card>
  );
}
